import type { RecordUnion } from 'components/framework/models/generated/types';
import Project from 'components/framework/models/Project';
import $ from 'jquery';
import {
  type GroupedMenu,
  type MenuContent,
  type MenuItem,
} from '../../ActionMenuItem';
import RecordAction from '../RecordAction';
import type ApplicationModel from 'components/framework/ApplicationModel';

export class AssignToTeamAction extends RecordAction<RecordUnion> {
  recordTypes = ['Epic', 'Feature', 'Requirement'];
  emptySubmenuContent = 'No teams available';

  get title() {
    return `Assign to team`;
  }

  get hasSubmenu() {
    return true;
  }

  async prepareSubmenuItems(): Promise<MenuContent> {
    const firstRecord = this.selectedRecords[0];
    if (!firstRecord) return [];

    const availableTeams = await Project.select('id', 'name')
      .merge({
        parent: ['id', 'name'],
        projects: ['id', 'name'],
      })
      .where({ teams: true })
      .all();

    const currentProjectId = firstRecord.project?.id;

    const groupedTeams = aggregateAndSortSubmenuItems({
      currentProjectId,
      teams: availableTeams,
    });

    if (Object.keys(groupedTeams).length === 1) {
      // no need for grouping in the submenu in this case
      return Object.values(groupedTeams)[0];
    }

    return groupedTeams;
  }

  showForRecord(record: ApplicationModel): record is RecordUnion {
    const isTeamAssignable =
      'assignToTeam' in record && typeof record.assignToTeam === 'function';

    return super.showForRecord(record) && isTeamAssignable && !record.team?.id;
  }

  showForSelectedRecords() {
    return !!(
      window.currentAccount?.teamworkOrDevelopFlavor &&
      window.currentProject?.id &&
      this.selectedRecords.every(
        record =>
          this.selectedRecords[0].project?.id === record.project?.id &&
          this.showForRecord(record)
      )
    );
  }

  async runForRecords(records, { id }) {
    if (!id) return;

    await this.mutationBatcher.execute(() =>
      records.map(record => {
        const prefix = record.typename.toLowerCase();
        $(`#${prefix}_${record.id}`).attr('data-team-id', id);

        return record.assignToTeam(
          { id },
          {
            batcher: this.mutationBatcher,
            resetAttributes: true,
          }
        );
      })
    );
  }
}

type TeamWithParentLine = Project & { parent: { id: string; name: string } };

export function aggregateAndSortSubmenuItems({
  currentProjectId,
  teams,
}: {
  currentProjectId: string;
  teams: Project[];
}) {
  const sortedTeams = teams.toSorted((a, b) => a.name.localeCompare(b.name));

  const relatedTeams = currentProjectId
    ? sortedTeams.filter(team =>
        team.projects?.some(project => project?.id === currentProjectId)
      )
    : [];

  const unRelatedTeams = sortedTeams.filter(
    team => !relatedTeams.some(rt => rt.id === team.id)
  );

  const teamsWithParentLines = unRelatedTeams.filter(
    (team): team is TeamWithParentLine => !!team.parent?.id
  );

  // Group teams in the submenu by their parent line
  const groupedByParentName = teamsWithParentLines.reduce<GroupedMenu>(
    (acc, team) => {
      const parentName = team.parent.name;
      const menuItem: MenuItem = {
        data: team,
        label: team.name,
      };
      if (!acc[parentName]) {
        acc[parentName] = [menuItem];
      } else if (!acc[parentName].includes(menuItem)) {
        acc[parentName].push(menuItem);
      }
      return acc;
    },
    {}
  );

  const sortedGroupedByParentName = Object.fromEntries(
    Object.keys(groupedByParentName)
      .toSorted((a, b) => a.localeCompare(b)) // Sort parent names alphabetically
      .map(groupName => [groupName.trim(), groupedByParentName[groupName]])
  );

  const otherTeams = unRelatedTeams.filter(
    team => !team.parent || !team.parent.id
  );

  // Note the space after "Related teams " and "Other teams "
  // is to ensure they don't conflict with any existing group names ( which will be trimmed )
  const groupedTeams: GroupedMenu = {
    ...(relatedTeams.length
      ? {
          'Related teams ': relatedTeams.map(team => ({
            data: team,
            label: team.name,
          })),
        }
      : {}),
    ...sortedGroupedByParentName,
    ...(otherTeams.length
      ? {
          'Other teams ': otherTeams.map(team => ({
            data: team,
            label: team.name,
          })),
        }
      : {}),
  };

  return groupedTeams;
}
